import { format as libFormat, parseISO, isDate, subYears, differenceInYears, startOfDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { getIsServerSide } from './isServerSide';

const MIN_AGE = 16;
const ADULT_AGE = 18;

export const getMinAgeDate = (currentDate: Date, minAge: number = MIN_AGE): Date =>
  startOfDay(subYears(currentDate, minAge));

export const isAgeValidForSignup = (dob: string): boolean => {
  if (!dob) return false;

  const birthDate = new Date(dob);

  if (Number?.isNaN(birthDate.getTime())) {
    return false;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  if (age < MIN_AGE) {
    return false;
  }

  return true;
};

export const isAgeValidForBooking = (dob: string | undefined, sailingStartDate: string): boolean => {
  if (!dob) {
    return false;
  }

  const MIN_AGE_BOOKING = 18;
  const birthDate = new Date(dob);

  if (Number?.isNaN(birthDate.getTime())) {
    return false;
  }

  const sailingDate = new Date(sailingStartDate);
  let age = sailingDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = sailingDate.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && sailingDate.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  if (age < MIN_AGE_BOOKING) {
    return false;
  }

  return true;
};

export const isSailDatePastDay = (sailDate: string): boolean => {
  if (!sailDate) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [year, month, day] = sailDate.split('-').map(Number);
  const inputDate = new Date(year, month - 1, day);

  return inputDate < today;
};

export const isPrimaryGuestIsAdultBySailingDate = (sailingDate: Date | string, guestDOB: Date | string): boolean => {
  const year = differenceInYears(new Date(sailingDate), new Date(guestDOB));
  return year >= ADULT_AGE;
};

export const parse = (str: string | null): Date => {
  if (str) {
    return parseISO(str);
  }
  return new Date();
};

export const format = (date: Date | string | null, formatString = 'yyyy-MM-dd'): string | undefined => {
  if (!date) {
    return undefined;
  }

  let dateToFormat: Date;
  if (isDate(date)) {
    dateToFormat = date as Date;
  } else if (typeof date === 'string') {
    dateToFormat = parse(date);
  } else {
    return undefined;
  }

  return libFormat(dateToFormat, formatString);
};

export const formatMonthLabel = (date: Date | string): string | undefined => format(date, 'MMM yyyy');

export const currentNYTime = (serverDate?: string): Date => {
  const utc = serverDate || new Date().toISOString();
  try {
    return utcToZonedTime(utc, 'America/New_York');
  } catch {
    return utcToZonedTime(utc, '-04:00');
  }
};

export const isUserAtLeastAge = (dob: Date | string, currentDate: Date | string, age: number): boolean => {
  const year = differenceInYears(new Date(currentDate), new Date(dob));
  return year >= age;
};

export const getMobileOperatingSystem = (): 'IOS' | 'android' | null => {
  if (getIsServerSide()) {
    return null;
  }

  const userAgent = navigator.userAgent || navigator.vendor || (window as { opera?: unknown }).opera;

  if (typeof userAgent === 'string') {
    if (/iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window)) {
      return 'IOS';
    }
    if (/android/i.test(userAgent)) {
      return 'android';
    }
  }

  return null;
};
